<template>
  <div class="step-template">
    <div class="row">
      <div class="col-md-6">
        <label>Email template</label>
        <base-input>
          <el-select
            :value="templateId"
            filterable
            placeholder="Choose a template"
            class="select-primary w-100"
            @input="$emit('update:template-id', $event)"
          >
            <el-option
              v-for="template in templates"
              :key="template.id"
              :value="template.id"
              :label="template.name"
              class="select-primary"
            />
          </el-select>
        </base-input>
      </div>
    </div>

    <p class="step-template__manage">
      <small>Need changes? <a href="#" @click.prevent="$emit('edit-templates')">Edit your templates</a> and come back.</small>
    </p>

    <div v-if="selectedTemplate" class="step-template__preview">
      <div class="step-template__preview-row">
        <span class="step-template__preview-label">Subject</span>
        <span class="step-template__preview-value">{{ subjectLine || 'No subject set' }}</span>
      </div>
      <div v-if="previewText" class="step-template__preview-row">
        <span class="step-template__preview-label">Preview text</span>
        <span class="step-template__preview-value">{{ previewText }}</span>
      </div>
    </div>

    <info-hint v-if="fromEmailBlocked" variant="warning" style="width: 100%" class="mt-4">
      This template sends from <strong>{{ selectedListRow.from_email }}</strong>, which is not on this group's verified sending domain. Every email in this campaign will fail to send. Update the from address in the template, or verify the domain under email and messaging settings.
    </info-hint>

    <info-hint v-else-if="selectedTemplate && domainBlockReason" variant="warning" style="width: 100%" class="mt-4">
      {{ domainBlockReason }}
    </info-hint>

    <info-hint v-if="unresolvableLabels.length" style="width: 100%" class="mt-4">
      This template uses <strong>{{ formatLabelList(unresolvableLabels) }} variables</strong>. Campaign emails aren't tied to a specific booking or purchase, so those fields will render empty. Pick a template written for marketing sends, or remove those variables first.
    </info-hint>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { unresolvableVariableLabels, formatLabelList } from './campaignVariables'

const props = defineProps({
  templates: { type: Array as PropType<any[]>, default: () => [] },
  templateId: { type: Number as PropType<number | null>, default: null },
  // full template row fetched by the parent after selection
  selectedTemplate: { type: Object as PropType<any>, default: null },
  // parent's verdict on the from address versus the group's verified domain
  domainBlockReason: { type: String, default: '' }
})

defineEmits(['update:template-id', 'edit-templates'])

function templateContentString(): string {
  const t = props.selectedTemplate
  if (!t) return ''
  const content = t.content_json ?? t.content ?? ''
  return typeof content === 'string' ? content : JSON.stringify(content)
}

const unresolvableLabels = computed(() => unresolvableVariableLabels(templateContentString()))

// The list payload carries the sending-domain verdict per template
// (can_activate / activation_block_reason), so a template whose from address
// cannot deliver is flagged before the campaign is sent.
const selectedListRow = computed(() => props.templates.find((t: any) => t.id === props.templateId) || null)
const fromEmailBlocked = computed(() => selectedListRow.value?.can_activate === false)

const settings = computed(() => {
  const t = props.selectedTemplate
  if (!t) return {}
  const contentJson = typeof t.content_json === 'string' ? safeParse(t.content_json) : t.content_json
  return contentJson?.settings || t
})

function safeParse(value: string) {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const subjectLine = computed(() => settings.value?.subject_line || props.selectedTemplate?.subject_line || '')
const previewText = computed(() => settings.value?.preview_text || props.selectedTemplate?.preview_text || '')
</script>

<script lang="ts">
export default { name: 'campaign-step-template' }
</script>

<style lang="scss" scoped>
.step-template {
  &__manage {
    color: rgba(255, 255, 255, 0.75);
    margin-top: 0.5rem;

    a {
      color: #e14eca;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__preview {
    margin-top: 1.5rem;
    padding: 1rem 1.25rem;
    border-radius: 0.4285rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.03);
  }

  &__preview-row {
    display: flex;
    gap: 0.75rem;

    & + & {
      margin-top: 0.5rem;
    }
  }

  // Mirrors the review step's row-label treatment so the two key/value
  // blocks read as one system
  &__preview-label {
    flex-shrink: 0;
    width: 110px;
    font-size: 0.875rem;
    color: hsla(0, 0%, 100%, 0.8);
  }

  &__preview-value {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.85);
  }
}

.white-content .step-template {
  &__manage {
    color: rgba(0, 0, 0, 0.7);
  }

  &__preview {
    border-color: rgba(0, 0, 0, 0.1);
    background: rgba(0, 0, 0, 0.02);
  }

  &__preview-label {
    color: rgba(0, 0, 0, 0.6);
  }

  &__preview-value {
    color: rgba(0, 0, 0, 0.8);
  }
}
</style>
